import React from 'react';
import { Button } from 'reactstrap';
import Dropzone from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx/xlsx.mjs';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import { Upload, CheckCircle, Download } from 'react-feather';
import { toast } from 'react-toastify';

const BulkCsvUpload = ({
  file,
  tableData,
  step,
  onFileAdd,
  onTableDataAdd,
  onTableDataRemove,
  onStepChange,
}) => {
  const onDrop = (acceptedFiles, rejectedFiles) => {
    const validFiles = acceptedFiles.filter((f) => {
      const ext = f.name.toLowerCase().split('.').pop();
      return ['csv', 'xls', 'xlsx'].includes(ext);
    });

    if (validFiles.length > 0) {
      handleAddFile(validFiles[0]);
    } else {
      const errorCode = rejectedFiles?.[0]?.errors?.[0]?.code;
      const errorMessage = rejectedFiles?.[0]?.errors?.[0]?.message;
      if (errorCode === 'file-too-large') {
        toast.warning(errorMessage, { autoClose: 3000 });
      } else {
        toast.warning('File type not supported. Please upload a .csv or .xlsx file.', { autoClose: 3000 });
      }
    }
  };

  const handleAddFile = (f) => {
    try {
      if (f?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || f?.name?.endsWith('.xlsx') || f?.name?.endsWith('.xls')) {
        handleAddFileExcel(f);
      } else {
        Papa.parse(f, {
          header: true,
          dynamicTyping: true,
          complete: (parseFile) => {
            onFileAdd(f);
            onTableDataAdd({
              data: parseFile?.data,
              columns: makeColumns(parseFile?.meta?.fields),
            });
          },
        });
      }
    } catch (error) {
      console.log('Error in Add File', error);
    }
  };

  const handleAddFileExcel = (f) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      const headers = parsedData[0];
      const rows = parsedData.slice(1).map((row) => {
        const rowData = {};
        headers.forEach((header, index) => {
          rowData[header] = row[index];
        });
        return rowData;
      });

      onFileAdd(f);
      onTableDataAdd({
        data: rows,
        columns: makeColumns(headers),
      });
    };
    reader.readAsArrayBuffer(f);
  };

  const makeColumns = (rawColumns) => {
    const filteredColumns = rawColumns?.filter((col) => col && !String(col).startsWith('_'));
    return filteredColumns?.map((col) => ({
      Header: String(col),
      accessor: String(col),
    }));
  };

  // Step 2: show uploaded status with edit option
  if (step === 2) {
    return (
      <div
        className="d-flex align-items-center justify-content-between my-3"
        style={{
          border: '1px solid #13CE66',
          backgroundColor: '#EFFAF7',
          borderRadius: '10px',
          padding: '0 1.5rem',
          height: '80px',
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div style={{ fontSize: '16px', fontWeight: 500, color: '#4F4F50' }}>
            File Uploaded
          </div>
          <CheckCircle color="green" size={18} />
        </div>
        <div
          role="button"
          onClick={onTableDataRemove}
          style={{ fontSize: '14px', textDecoration: 'underline', cursor: 'pointer' }}
        >
          Edit
        </div>
      </div>
    );
  }

  // Step 1: show file upload or preview
  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <div
          style={{
            width: '20px',
            height: '20px',
            backgroundColor: '#0d6efd',
            color: 'white',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          1
        </div>
        <div style={{ fontSize: '18px', fontWeight: 500, color: '#333' }}>
          Upload your CSV file
        </div>
      </div>

      <div className="d-flex align-items-start justify-content-between mb-3">
        <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
          Upload a CSV or Excel file with columns: <strong>type</strong> (brand/retailer),{' '}
          <strong>pitti_id</strong>, <strong>email</strong>, <strong>name</strong>,{' '}
          <strong>password</strong>, <strong>role</strong> (optional), <strong>phone</strong> (optional)
        </p>
        <a
          href="/pitti-bulk-invite-example.csv"
          download="pitti-bulk-invite-example.csv"
          className="d-flex align-items-center gap-1 text-dark flex-shrink-0 ms-3"
          style={{ fontSize: '14px', textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          <Download size={16} /> Download example file (.csv)
        </a>
      </div>

      {!file ? (
        <Dropzone accept={['.csv', '.xls', '.xlsx']} onDrop={onDrop}>
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps()}
              style={{
                width: '100%',
                border: '2px dashed #CDCDCD',
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                backgroundColor: '#FDFDFD',
                marginBottom: '1rem',
                cursor: 'pointer',
              }}
            >
              <input {...getInputProps()} />
              <div className="d-flex flex-column align-items-center px-4 py-3">
                <Upload size={40} color="#999" />
                <div className="fw-semibold mt-2">Upload a file</div>
                <div style={{ color: '#999', fontSize: '13px' }}>Select a .csv or .xlsx file from your computer</div>
              </div>
            </div>
          )}
        </Dropzone>
      ) : (
        <div>
          <div
            className="d-flex align-items-center justify-content-between my-3"
            style={{
              border: '1px solid #13CE66',
              backgroundColor: '#EFFAF7',
              borderRadius: '10px',
              padding: '0 1.5rem',
              height: '70px',
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <div style={{ fontSize: '16px', fontWeight: 500, color: '#4F4F50' }}>
                Uploaded
              </div>
              <CheckCircle color="green" size={18} />
            </div>
            <div
              role="button"
              onClick={onTableDataRemove}
              style={{ fontSize: '14px', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Delete File
            </div>
          </div>

          {tableData && (
            <div>
              <h6 className="fw-semibold">Preview of your file</h6>
              <ReactTable data={tableData?.data} columns={tableData?.columns} defaultPageSize={5} />
              <div className="py-3 d-flex justify-content-center align-items-center">
                <Button color="primary" onClick={() => onStepChange(2)}>
                  <span style={{ fontSize: '15px', fontWeight: 500 }}>Confirm your file</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkCsvUpload;
